/**
 * LayerReviewPanel — shown after a KMZ-imported boundary is confirmed, when the
 * parser surfaced additional features (utilities, constraints, planned zones).
 *
 * Pure UI: caller owns the parsed-layer list and receives the user-selected
 * subset back via onImport. Skip discards layers but keeps the boundary.
 *
 * The map preview is opt-in (`showMap`, default true). Tests pass false to
 * avoid pulling in the @vis.gl/react-google-maps runtime, which expects
 * Google's JS API and won't load in jsdom.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { ChevronDown, ChevronRight, Circle, Hexagon, Spline } from 'lucide-react';

import { Button } from '../ui/button';
import { LAYER_COLORS, CATEGORY_LABELS, CATEGORY_ORDER } from '../../config/siteLayerColors';
import { computeLayerContentHash, type ParsedLayer } from '../../services/kmzParser';
import type { SiteLayer, SiteLayerCategory } from '../../types/site';

export interface LayerReviewPanelProps {
  parsedLayers: ParsedLayer[];
  /** Confirmed site boundary, drawn on the preview map for reference. */
  boundary: { lat: number; lng: number }[];
  /** Render the live map preview. Defaults to true; tests pass false. */
  showMap?: boolean;
  /** Fired with the SiteLayer[] to commit. Layers are tagged source='kmz-import' here. */
  onImport: (selected: SiteLayer[]) => void;
  /** Fired when the user discards parsed layers (boundary still imports). */
  onSkip: () => void;
  /**
   * Unified-import (AddMapLayersDialog) — allow per-row rename + category
   * reassignment before commit. Defaults off so every legacy KMZ surface
   * keeps its read-only review.
   */
  editable?: boolean;
  /**
   * Row → SiteLayer converter override. The CAD path supplies one that maps
   * rows back to prebuilt `cad-{sha}-{handle}` SiteLayers (deterministic ids
   * feed the id-based CAD merge); default is {@link parsedToSiteLayer}.
   * Receives the row WITH any user edits applied, plus its original index.
   */
  toSiteLayer?: (layer: ParsedLayer, index: number) => SiteLayer;
}

export function LayerReviewPanel({
  parsedLayers,
  boundary,
  showMap = true,
  onImport,
  onSkip,
  editable = false,
  toSiteLayer,
}: LayerReviewPanelProps) {
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(
    () => new Set(parsedLayers.map((_, i) => i)),
  );
  const [collapsedCategories, setCollapsedCategories] = useState<Set<SiteLayerCategory>>(
    () => new Set(),
  );
  /** Per-row user edits (rename / recategorize), keyed by original index. */
  const [rowOverrides, setRowOverrides] = useState<
    Map<number, { name?: string; category?: SiteLayerCategory }>
  >(() => new Map());

  const setRowOverride = (index: number, patch: { name?: string; category?: SiteLayerCategory }) => {
    setRowOverrides((prev) => {
      const next = new Map(prev);
      next.set(index, { ...next.get(index), ...patch });
      return next;
    });
  };

  /** parsedLayers with user edits applied — every consumer below reads this. */
  const effectiveLayers = useMemo(
    () => parsedLayers.map((layer, i) => {
      const o = rowOverrides.get(i);
      if (!o) return layer;
      const name = o.name !== undefined && o.name.trim() !== '' ? o.name : layer.name;
      return { ...layer, name, ...(o.category !== undefined ? { category: o.category } : {}) };
    }),
    [parsedLayers, rowOverrides],
  );

  const grouped = useMemo(() => {
    const m = new Map<SiteLayerCategory, Array<{ layer: ParsedLayer; index: number }>>();
    effectiveLayers.forEach((layer, index) => {
      const arr = m.get(layer.category) ?? [];
      arr.push({ layer, index });
      m.set(layer.category, arr);
    });
    return m;
  }, [effectiveLayers]);

  const toggleRow = (index: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleCategory = (cat: SiteLayerCategory) => {
    const indexes = (grouped.get(cat) ?? []).map((r) => r.index);
    const allSelected = indexes.every((i) => selectedIndexes.has(i));
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (allSelected) for (const i of indexes) next.delete(i);
      else for (const i of indexes) next.add(i);
      return next;
    });
  };

  const toggleCollapsed = (cat: SiteLayerCategory) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const handleImport = () => {
    const convert = toSiteLayer ?? ((l: ParsedLayer) => parsedToSiteLayer(l));
    const layers = effectiveLayers
      .map((l, i) => ({ l, i }))
      .filter(({ i }) => selectedIndexes.has(i))
      .map(({ l, i }) => convert(l, i));
    onImport(layers);
  };

  const selectedLayersForMap = useMemo(
    () => effectiveLayers.filter((_, i) => selectedIndexes.has(i)),
    [effectiveLayers, selectedIndexes],
  );

  return (
    <div
      data-testid="layer-review-panel"
      className="space-y-4 border border-border-subtle bg-state-hover p-4"
    >
      <div className="text-center space-y-1">
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-text-primary">
          {parsedLayers.length} additional feature{parsedLayers.length === 1 ? '' : 's'} detected. Review and import.
        </h4>
        <p className="text-[10px] text-text-muted uppercase tracking-widest">
          Toggle individual rows or whole categories. The map updates live.
        </p>
      </div>

      <div className="space-y-2">
        {CATEGORY_ORDER.map((cat) => {
          const rows = grouped.get(cat);
          if (!rows || rows.length === 0) return null;
          const isCollapsed = collapsedCategories.has(cat);
          const checkedCount = rows.filter((r) => selectedIndexes.has(r.index)).length;
          const Chevron = isCollapsed ? ChevronRight : ChevronDown;
          const color = LAYER_COLORS[cat];
          const label = CATEGORY_LABELS[cat];
          return (
            <div
              key={cat}
              data-testid={`category-section-${cat}`}
              className="border border-border-subtle bg-background"
            >
              <div className="flex items-center gap-2 px-3 py-2 type-section">
                <button
                  type="button"
                  onClick={() => toggleCollapsed(cat)}
                  className="flex items-center gap-2 flex-1 text-left hover:opacity-70"
                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${label}`}
                >
                  <Chevron className="w-3 h-3 text-text-secondary" />
                  <span
                    className="w-3 h-3 inline-block"
                    style={{ backgroundColor: color }}
                    aria-hidden
                  />
                  <span className="flex-1 text-text-primary">{label}</span>
                  <span className="text-text-muted">{checkedCount} / {rows.length}</span>
                </button>
                <input
                  type="checkbox"
                  aria-label={`Toggle all ${label}`}
                  data-testid={`category-toggle-${cat}`}
                  className="ml-2 w-3 h-3 accent-primary cursor-pointer"
                  checked={checkedCount === rows.length}
                  ref={(el) => {
                    if (el) el.indeterminate = checkedCount > 0 && checkedCount < rows.length;
                  }}
                  onChange={() => toggleCategory(cat)}
                />
              </div>
              {!isCollapsed && (
                <div className="border-t border-border-subtle">
                  {rows.map(({ layer, index }) => (
                    <LayerRow
                      key={`${cat}-${index}`}
                      layer={layer}
                      checked={selectedIndexes.has(index)}
                      onToggle={() => toggleRow(index)}
                      testIndex={index}
                      editable={editable}
                      onRename={(name) => setRowOverride(index, { name })}
                      onRecategorize={(category) => setRowOverride(index, { category })}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showMap && boundary.length > 0 && (
        <LayerMapPreview boundary={boundary} layers={selectedLayersForMap} />
      )}

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          className="h-9 rounded-md type-meta"
          onClick={onSkip}
          data-testid="skip-button"
        >
          Skip — Discard Layers
        </Button>
        <div className="flex-1" />
        <Button
          className="h-9 rounded-md type-meta font-bold"
          disabled={selectedIndexes.size === 0}
          onClick={handleImport}
          data-testid="import-button"
        >
          Import {selectedIndexes.size} Selected
        </Button>
      </div>
    </div>
  );
}

/* ── Row ──────────────────────────────────────────────────────────────── */

function LayerRow({
  layer,
  checked,
  onToggle,
  testIndex,
  editable = false,
  onRename,
  onRecategorize,
}: {
  layer: ParsedLayer;
  checked: boolean;
  onToggle: () => void;
  testIndex: number;
  editable?: boolean;
  onRename?: (name: string) => void;
  onRecategorize?: (category: SiteLayerCategory) => void;
}) {
  const Icon = layer.geometry.type === 'point' ? Circle : layer.geometry.type === 'line' ? Spline : Hexagon;
  const color = LAYER_COLORS[layer.category];
  const description = (layer.description ?? '').replace(/\s+/g, ' ').trim();
  return (
    <label
      data-testid={`layer-row-${testIndex}`}
      className="flex items-start gap-3 px-3 py-2 border-t border-border-subtle first:border-t-0 cursor-pointer hover:bg-state-hover"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-0.5 w-3 h-3 accent-primary"
        data-testid={`layer-checkbox-${testIndex}`}
      />
      <span className="w-3 h-3 inline-block mt-1" style={{ backgroundColor: color }} aria-hidden />
      <Icon className="w-3 h-3 mt-1 text-text-secondary shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        {editable ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              defaultValue={layer.name}
              aria-label={`Rename ${layer.name}`}
              data-testid={`layer-rename-${testIndex}`}
              className="flex-1 min-w-0 bg-background border border-border-subtle rounded px-2 py-0.5 type-section text-text-primary"
              onClick={(e) => e.preventDefault()}
              onChange={(e) => onRename?.(e.target.value)}
            />
            <select
              value={layer.category}
              aria-label={`Category for ${layer.name}`}
              data-testid={`layer-category-${testIndex}`}
              className="bg-background border border-border-subtle rounded px-1 py-0.5 type-meta text-text-secondary"
              onClick={(e) => e.preventDefault()}
              onChange={(e) => onRecategorize?.(e.target.value as SiteLayerCategory)}
            >
              {CATEGORY_ORDER.map((cat) => (
                <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="type-section text-text-primary truncate">
            {layer.name}
          </div>
        )}
        {description !== '' && (
          <div className="type-meta text-text-muted truncate">{description}</div>
        )}
        {layer.folderPath.length > 0 && (
          <div className="type-meta text-text-disabled uppercase tracking-widest">
            {layer.folderPath.join(' / ')}
          </div>
        )}
      </div>
    </label>
  );
}

/* ── Map preview (uses the parent APIProvider) ────────────────────────── */

function LayerMapPreview({
  boundary,
  layers,
}: {
  boundary: { lat: number; lng: number }[];
  layers: ParsedLayer[];
}) {
  const center = useMemo(() => computeCenter(boundary, layers), [boundary, layers]);
  return (
    <div className="h-72 w-full" data-testid="layer-map-preview">
      <GoogleMap
        mapId="layer-review-map"
        defaultCenter={center}
        defaultZoom={14}
        disableDefaultUI
        gestureHandling="cooperative"
      >
        <MapOverlays boundary={boundary} layers={layers} />
      </GoogleMap>
    </div>
  );
}

function MapOverlays({
  boundary,
  layers,
}: {
  boundary: { lat: number; lng: number }[];
  layers: ParsedLayer[];
}) {
  const map = useMap('layer-review-map');
  const overlaysRef = useRef<Array<google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement>>([]);

  useEffect(() => {
    if (!map || typeof google === 'undefined' || !google.maps) return;

    // AdvancedMarkerElement uses `.map = null` for removal; Polygon/Polyline keep
    // `setMap(null)`. Branch on which API exists.
    overlaysRef.current.forEach(removeOverlayFromMap);
    overlaysRef.current = [];

    if (boundary.length > 0) {
      overlaysRef.current.push(
        new google.maps.Polygon({
          paths: boundary,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          fillOpacity: 0.05,
          fillColor: '#ffffff',
          map,
        }),
      );
    }

    // AdvancedMarkerElement lives in the `marker` library; lazy-load it before
    // creating point markers. Polygons + Polylines don't need it so they render
    // synchronously in the loop below.
    let cancelled = false;
    void (async () => {
      const markerLib = google.maps.marker ?? (await google.maps.importLibrary('marker') as google.maps.MarkerLibrary);
      if (cancelled) return;

      for (const layer of layers) {
        const color = LAYER_COLORS[layer.category];
        if (layer.geometry.type === 'polygon') {
          overlaysRef.current.push(
            new google.maps.Polygon({
              paths: layer.geometry.coordinates,
              strokeColor: color,
              fillColor: color,
              fillOpacity: 0.15,
              strokeWeight: 2,
              map,
            }),
          );
        } else if (layer.geometry.type === 'line') {
          overlaysRef.current.push(
            new google.maps.Polyline({
              path: layer.geometry.coordinates,
              strokeColor: color,
              strokeWeight: 2,
              map,
            }),
          );
        } else if (layer.geometry.type === 'point' && layer.geometry.coordinates[0]) {
          // Migrated from `new google.maps.Marker({ icon: { path: CIRCLE, ... }})`
          // (deprecated Feb 2024) to AdvancedMarkerElement with a small styled
          // <div> as content. Matches the previous 10px colored-dot appearance.
          overlaysRef.current.push(
            new markerLib.AdvancedMarkerElement({
              position: layer.geometry.coordinates[0],
              content: createReviewMarkerElement(color),
              map,
            }),
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      overlaysRef.current.forEach(removeOverlayFromMap);
      overlaysRef.current = [];
    };
  }, [map, boundary, layers]);

  return null;
}

/**
 * Build the small colored dot that used to come from
 * `icon: { path: SymbolPath.CIRCLE, fillColor, scale: 5, strokeColor: '#fff' }`.
 * Matches the previous appearance (10px diameter, 1px white stroke — the old
 * code didn't set strokeWeight on this surface, so default 1px applies).
 */
function createReviewMarkerElement(color: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = [
    'width:10px',
    'height:10px',
    'border-radius:50%',
    `background-color:${color}`,
    'border:1px solid #ffffff',
    'box-sizing:border-box',
  ].join(';') + ';';
  return el;
}

/**
 * Polygon / Polyline use `setMap(null)`; AdvancedMarkerElement uses `.map = null`.
 * Branch on which surface exists rather than narrowing each call site.
 */
function removeOverlayFromMap(
  overlay: google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement,
): void {
  if ('position' in overlay && !('getPath' in overlay) && !('getPaths' in overlay)) {
    // AdvancedMarkerElement — settable property, not a method.
    (overlay as google.maps.marker.AdvancedMarkerElement).map = null;
  } else {
    (overlay as google.maps.Polygon | google.maps.Polyline).setMap(null);
  }
}

/* ── Helpers ──────────────────────────────────────────────────────────── */

/** Exported for tests — must stay loop-based (no spread) for dense geometries. */
export function computeCenter(
  boundary: { lat: number; lng: number }[],
  layers: ParsedLayer[],
): { lat: number; lng: number } {
  // Fold sums directly — spreading dense geometries (>~65k vertices) into
  // push() overflows the call stack (each element becomes a call argument).
  let sumLat = 0;
  let sumLng = 0;
  let count = 0;
  for (const p of boundary) {
    sumLat += p.lat;
    sumLng += p.lng;
    count += 1;
  }
  for (const l of layers) {
    for (const p of l.geometry.coordinates) {
      sumLat += p.lat;
      sumLng += p.lng;
      count += 1;
    }
  }
  if (count === 0) return { lat: 0, lng: 0 };
  return { lat: sumLat / count, lng: sumLng / count };
}

/**
 * R-131-2 — Convert a ParsedLayer to a SiteLayer with a *deterministic* id.
 *
 * The pre-R-131-2 id was `layer-${Date.now()}-${Math.random().slice(2,8)}` —
 * re-importing the same KMZ produced a brand-new id for every feature,
 * which (combined with `mergeImportedLayers` only deduping by contentHash)
 * meant re-uploads silently appended duplicate copies of every layer. With
 * id stamped from `computeLayerContentHash` the id is stable across re-
 * imports of the same KML feature, so the id-keyed Firestore subcollection
 * upsert in `saveSiteLayersBatch` becomes truly idempotent.
 *
 * `contentHash` is also stamped here so `mergeImportedLayers` short-
 * circuits on its existing fast path (hash-equal → skip) without having
 * to recompute it inline.
 */
/** Deterministic id prefix per import source (unknown sources fall back to
 *  the historical 'kmz' prefix so legacy ids never shift). */
const SOURCE_ID_PREFIX: Partial<Record<SiteLayer['source'], string>> = {
  'kmz-import': 'kmz',
  'geojson-import': 'geojson',
  'shapefile-import': 'shp',
};

export function parsedToSiteLayer(layer: ParsedLayer): SiteLayer {
  const importedAt = new Date().toISOString();
  // Unified-import formats stamp their source via `sourceHint`; the legacy
  // KMZ surfaces never set it, so their output stays byte-identical.
  const source = layer.sourceHint ?? 'kmz-import';
  const provisional: SiteLayer = {
    id: 'pending',
    name: layer.name,
    category: layer.category,
    geometry: layer.geometry,
    description: layer.description,
    source,
    importedAt,
    visible: true,
    ...(layer.attributes ? { attributes: layer.attributes } : {}),
    evidence: {
      use: 'site-truth',
      source: { providerId: source, retrievedAt: importedAt },
    },
  };
  const hash = computeLayerContentHash(provisional);
  return {
    ...provisional,
    id: `${SOURCE_ID_PREFIX[source] ?? 'kmz'}-${hash}`,
    contentHash: hash,
  };
}
